/**
 * In this file the word "entity" refers to a feature "record" (e.g., an Action, an Exception, a Third Party Service IRQ, etc.).
 *
 * This page renders a single pane of glass for a User to see every entity that is assigned to them.
 * In the interest of simplifying/reducing back-end queries, the NotificationsApi query is used to get entities that have due dates.
 * For entities without due dates, additional feature-specific API queries are made.
 * Regardless of the data source, the entities are then transformed into a common Assignment interface so they can be displayed in a single table.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton } from '@mui/material';
import { type JSX, useContext, useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { ActionsApi } from 'Api/Actions/ActionsApi';
import { GovernanceApi } from 'Api/Governance/GovernanceApi';
import { NotificationsApi } from 'Api/Notifications/NotificationsApi';
import { RiskRegisterApi } from 'Api/RiskRegister/RiskRegisterApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACComponent } from 'Components/Context/RBACComponent';
import { RBACContext, Role, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { CustomDateModal } from 'Components/Modal/CustomDateModal/CustomDateModal';
import { PageLayoutDashboard } from 'Components/PageLayout/PageLayoutDashboard';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { ICON_ALERT_WARNING, ICON_NEUTRAL, ICON_UNKNOWN } from 'Config/Icons';
import { ACTION_DETAILS, GOVERNANCE_DETAILS, RISK_DETAILS, TPRM_ASSESSMENT, TPRM_FOLDER_DETAILS, TPRM_INHERENT_RISK_QUESTIONNAIRE, TPRM_SERVICE_DASHBOARD, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, UCRM_ASSESSMENT, UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_FOLDER_DETAILS, UCRM_INHERENT_RISK_QUESTIONNAIRE, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getNotificationDisplayName } from 'Helpers/NotificationFormatter/NotificationFormatter';
import { getNotificationUrl } from 'Helpers/URLBuilder/URLBuilder';
import { CONTROL_AUTOMATION_ROLES, RISK_ROLES, TPRM_ROLES, UCRM_ROLES } from 'Helpers/UserUtils';
import { useDateFilter } from 'Hooks/DateFilter';
import { Action } from 'Models/Actions';
import { AssessmentSubjectBase, OrganizationResponseWithAssessmentSubjectsBase } from 'Models/AssessmentSubject';
import { AdvancedFeature } from 'Models/ClientDetailsModel';
import { DateOptionSelectOption, MyAssignmentsDateRangeOptions } from 'Models/Dashboards';
import { DueDiligenceAssessmentSummary } from 'Models/DueDiligence';
import { AssessmentWorkflowState } from 'Models/DueDiligenceWorkflow';
import { ExceptionStatus } from 'Models/Exceptions';
import { FolderVersion } from 'Models/Folders';
import { GovernanceVersion } from 'Models/Governance';
import { NotificationType, UserNotification } from 'Models/Notification';
import { AssessmentState } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskResponse } from 'Models/RiskRegister';
import { getOrganizationAssessmentSubjects } from 'Models/UcrmTprm';
import { UserResponse } from 'Models/User';

import styles from './MyAssignments.module.css';

/** Enumerates the various types of Assignments that a User can have. This is somewhat related to NotificationType, although the values are different since Notifications always have a due date whereas Assignments may not. */
enum AssignmentType {
    ACTION = 'Action',
    CONTROL_ASSESSMENT = 'Control Assessment',
    EXCEPTION = 'Exception',
    GOVERNANCE = 'Governance',
    ISSUE = 'Issue',
    UCRM_ASSESSMENT = 'AI Governance Assessment',
    UCRM_BUSINESS = 'AI Governance Business',
    UCRM_DUE_DILIGENCE_WORKFLOW = 'AI Governance Due Diligence Workflow',
    UCRM_FOLDER = 'AI Governance Folder',
    UCRM_USE_CASE_IRQ = 'AI Governance Inherent Risk Questionnaire',
    RISK = 'Risk',
    THIRD_PARTY = 'TPRM Third Party',
    THIRD_PARTY_ASSESSMENT = 'TPRM Assessment',
    THIRD_PARTY_FOLDER = 'TPRM Folder',
    THIRD_PARTY_DUE_DILIGENCE_WORKFLOW = 'TPRM Due Diligence Workflow',
    THIRD_PARTY_SERVICE_IRQ = 'TPRM Inherent Risk Questionnaire',
}

/**
 * This creates a common representation of the entities that can be assigned to a User.
 * @param dueDate The date the entity is due. Optional, since not all entities have a due date.
 * @param id The unique identifier of the entity.
 * @param inputRequired Whether the entity is currently awaiting input from the assigned User. The undefined state is used when the User is a delegate and it is unknown whether the entity is awaiting input from them or the entity owner.
 * @param title The display title of the entity.
 * @param type The type of entity (e.g., Action, Third-Party Service IRQ, etc.).
 * @param url The URL to view the entity details.
 */
interface Assignment {
    dueDate?: string;
    id: string;
    inputRequired?: boolean;
    title: string;
    type: AssignmentType;
    url: string;
}

/** The Assignment types and routes for a module (TPRM or UCRM) whose Assignments are parsed by `parseModuleAssignments`. */
interface ModuleAssignmentConfiguration {
    assessmentPathTemplate: string;
    assessmentType: AssignmentType;
    dueDiligenceWorkflowPathTemplate: string;
    dueDiligenceWorkflowType: AssignmentType;
    folderPathTemplate: string;
    folderType: AssignmentType;
    irqPathTemplate: string;
    irqType: AssignmentType;
    module: DueDiligenceModule;
    organizationListingPathTemplate: string;
    organizationType: AssignmentType;
}

const TPRM_ASSIGNMENT_CONFIGURATION: ModuleAssignmentConfiguration = {
    assessmentPathTemplate: TPRM_ASSESSMENT,
    assessmentType: AssignmentType.THIRD_PARTY_ASSESSMENT,
    dueDiligenceWorkflowPathTemplate: TPRM_SERVICE_DASHBOARD,
    dueDiligenceWorkflowType: AssignmentType.THIRD_PARTY_DUE_DILIGENCE_WORKFLOW,
    folderPathTemplate: TPRM_FOLDER_DETAILS,
    folderType: AssignmentType.THIRD_PARTY_FOLDER,
    irqPathTemplate: TPRM_INHERENT_RISK_QUESTIONNAIRE,
    irqType: AssignmentType.THIRD_PARTY_SERVICE_IRQ,
    module: ProductModule.TPRM,
    organizationListingPathTemplate: TPRM_THIRD_PARTIES_AND_SERVICES_LISTING,
    organizationType: AssignmentType.THIRD_PARTY,
};

const UCRM_ASSIGNMENT_CONFIGURATION: ModuleAssignmentConfiguration = {
    assessmentPathTemplate: UCRM_ASSESSMENT,
    assessmentType: AssignmentType.UCRM_ASSESSMENT,
    dueDiligenceWorkflowPathTemplate: UCRM_USE_CASE_DASHBOARD,
    dueDiligenceWorkflowType: AssignmentType.UCRM_DUE_DILIGENCE_WORKFLOW,
    folderPathTemplate: UCRM_FOLDER_DETAILS,
    folderType: AssignmentType.UCRM_FOLDER,
    irqPathTemplate: UCRM_INHERENT_RISK_QUESTIONNAIRE,
    irqType: AssignmentType.UCRM_USE_CASE_IRQ,
    module: ProductModule.UCRM,
    organizationListingPathTemplate: UCRM_BUSINESSES_AND_USE_CASES_LISTING,
    organizationType: AssignmentType.UCRM_BUSINESS,
};

/**
 * Builds a module's (TPRM or UCRM) Assignments that are assigned to the given User and do not have a due date: organizations (Third Parties/Businesses), Folders, IRQs, Due Diligence Assessments, and Due Diligence Workflows.
 * Items that have a due date are excluded, since those are returned by the NotificationsApi query instead.
 */
const parseModuleAssignments = (configuration: ModuleAssignmentConfiguration, organizations: OrganizationResponseWithAssessmentSubjectsBase[], folders: FolderVersion[], assessmentSummaries: DueDiligenceAssessmentSummary[], userId: string): Assignment[] => {
    const assignments: Assignment[] = [];

    // Track the names of organizations managed by the User, so that their Folders can be included (and titled) without an extra API call.
    const managedOrganizationNames = new Map<string, string>();
    // Build a map of assessment subject IDs to assessment subjects so Assessment titles can be constructed without an extra API call. Unlike the assignments below, this is populated for every assessment subject (not just those managed by the User), because an Assessment is assigned via its own owner/delegates, which may be different users than the assessment subject's manager.
    const subjectMap = new Map<string, AssessmentSubjectBase>();

    for (const organization of organizations) {
        if (organization.vendor_manager_user_id === userId) {
            managedOrganizationNames.set(organization.id, organization.name);
            assignments.push({
                id: organization.id,
                inputRequired: false,
                title: organization.name,
                type: configuration.organizationType,
                url: generatePath(configuration.organizationListingPathTemplate),
            });
        }

        for (const subject of getOrganizationAssessmentSubjects(configuration.module, organization)) {
            subjectMap.set(subject.id, subject);

            // Only include assessment subjects where the manager is the User.
            if (subject.vendor_service_manager_user_id !== userId) {
                continue;
            }

            if (!subject.assessment_workflow_setup.due_date) {
                assignments.push({
                    id: `${subject.id} - ${configuration.dueDiligenceWorkflowType}`,
                    inputRequired: false,
                    title: `${subject.vendor_name} - ${subject.name}`,
                    type: configuration.dueDiligenceWorkflowType,
                    url: generatePath(configuration.dueDiligenceWorkflowPathTemplate, { third_party_id: subject.vendor_id, service_id: subject.id }),
                });
            }

            if (!subject.assessment_workflow_setup.irq_target_completion_date) {
                assignments.push({
                    id: `${subject.id} - ${configuration.irqType}`,
                    inputRequired: false,
                    title: `${subject.vendor_name} - ${subject.name}`,
                    type: configuration.irqType,
                    url: generatePath(configuration.irqPathTemplate, { third_party_id: subject.vendor_id, service_id: subject.id }),
                });
            }
        }
    }

    // Folders are implicitly assigned to the manager of the organization they belong to.
    folders
        .filter((folderVersion) => !folderVersion.expiration_date && managedOrganizationNames.has(folderVersion.third_party_id))
        .forEach((folderVersion) => {
            assignments.push({
                id: folderVersion.id,
                inputRequired: false,
                title: `${managedOrganizationNames.get(folderVersion.third_party_id)!} - ${folderVersion.name}`,
                type: configuration.folderType,
                url: generatePath(configuration.folderPathTemplate, { third_party_id: folderVersion.third_party_id, folder_id: folderVersion.id }),
            });
        });

    for (const assessment of assessmentSummaries) {
        if (assessment.assessment_due_date) {
            continue;
        }

        // Exclude assessments that do not exist from the user's perspective. Once a due diligence workflow is completed, its assessments still exist in the database. But from the user's perspective, they do not exist again until the next workflow is started and the IRQ is submitted.
        const subject = subjectMap.get(assessment.service_id)!;
        if (subject.assessment_workflow_data.state !== AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE) {
            continue;
        }

        const isOwner = assessment.owner_id === userId;
        const isDelegate = assessment.delegate_ids.includes(userId);
        if (!isOwner && !isDelegate) {
            continue;
        }

        // A submitted Assessment is not awaiting input. Otherwise, the owner's input is required, while a delegate's input requirement is unknown (we cannot programmatically know if input is required from the owner or a delegate).
        const inputRequired = assessment.assessment_submitted_timestamp ? false : isOwner ? true : undefined;

        assignments.push({
            id: assessment.id,
            inputRequired,
            title: `${assessment.name} (${subject.vendor_name} - ${subject.name})`,
            type: configuration.assessmentType,
            url: generatePath(configuration.assessmentPathTemplate, { third_party_id: assessment.third_party_id, service_id: assessment.service_id, assessment_id: assessment.id }),
        });
    }

    return assignments;
};

// Filters for the "Input Required" column.
export enum InputRequiredFilterValues {
    ANY_WORKFLOW_STATE = 'Any Workflow State',
    AWAITING_INPUT = 'Awaiting Input',
}

// Filters for the "Due Date" column.
export enum DueDateFilterValues {
    HIDE_NA_DUE_DATES = 'Hide N/A Due Dates',
    SHOW_NA_DUE_DATES = 'Show N/A Due Dates',
}

enum Modals {
    NONE,
    CUSTOM_DATE,
}

export interface MyAssignmentsProps {
    actionsApi: ActionsApi;
    authHandler: AuthHandler;
    governanceApi: GovernanceApi;
    notificationsApi: NotificationsApi;
    ucrmApi: ClientUcrmApi;
    ucrmAssessmentApi: UcrmAssessmentApi;
    riskRegisterApi: RiskRegisterApi;
    tprmApi: ClientTPRMApi;
    tprmAssessmentApi: TprmAssessmentApi;
}

export const MyAssignments = (props: MyAssignmentsProps) => {
    const cachedData = useCachedData();
    const rbacContext = useContext(RBACContext);

    const clientHasUcrm = cachedData.clientHasAdvancedFeature([AdvancedFeature.UCRM]);

    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.NEXT_ONE_MONTH);

    // Page state.
    const [apiQueriesInProgress, setApiQueriesInProgress] = useState<number>(0);
    const [error, setError] = useState<Error>();
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);

    // Various text that is dependent on whether the User viewing the page is looking at their own Assignments or someone else's.
    const [pageTitle, setPageTitle] = useState<string>('');
    const [userHasNoAssignmentsText, setUserHasNoAssignmentsText] = useState<string>('');
    const [userInputNotRequiredText, setUserInputNotRequiredText] = useState<string>('');
    const [userInputRequiredText, setUserInputRequiredText] = useState<string>('');
    const [userInputRequiredUnknownText, setUserInputRequiredUnknownText] = useState<string>('');

    // Table filtering (external filters only - internal filters handled by Table).
    const [selectedUser, setSelectedUser] = useState<UserResponse>(cachedData.users.find((user) => user.cognito_subject === props.authHandler.getAuthenticatedUserSubject())!);

    // Table data.
    const [actionAssignments, setActionAssignments] = useState<Assignment[]>([]);
    const [governanceAssignments, setGovernanceAssignments] = useState<Assignment[]>([]);
    const [notificationAssignments, setNotificationAssignments] = useState<Assignment[]>([]);
    const [ucrmAssignments, setUcrmAssignments] = useState<Assignment[]>([]);
    const [riskAssignments, setRiskAssignments] = useState<Assignment[]>([]);
    const [tprmAssignments, setTprmAssignments] = useState<Assignment[]>([]);

    /** When the selectedUser changes, update various text that is dependent on the User. */
    useEffect(() => {
        if (selectedUser.cognito_subject === props.authHandler.getAuthenticatedUserSubject()) {
            setPageTitle('My Assignments');
            setUserHasNoAssignmentsText('You have no assignments matching the selected criteria.');
            setUserInputNotRequiredText("Your input is not required. Either this assignment's workflow is awaiting input from someone else, or this assignment does not have a workflow.");
            setUserInputRequiredText("Your input is required on this assignment's workflow.");
            setUserInputRequiredUnknownText("Your input may be required on this assignment's workflow since you are a delegate.");
        } else {
            const otherUsersName = `${selectedUser.first_name} ${selectedUser.last_name}`;
            setPageTitle(`${otherUsersName}'s Assignments`);
            setUserHasNoAssignmentsText(`${otherUsersName} has no assignments matching the selected criteria.`);
            setUserInputNotRequiredText(`${otherUsersName}'s input is not required. Either this assignment's workflow is awaiting input from someone else, or this assignment does not have a workflow.`);
            setUserInputRequiredText(`${otherUsersName}'s input is required on this assignment's workflow.`);
            setUserInputRequiredUnknownText(`${otherUsersName}'s input may be required on this assignment's workflow since they are a delegate.`);
        }
    }, [props.authHandler, selectedUser]);

    /** When the component mounts or when the selectedUser changes, get Actions that do not have a due date and are assigned to the selectedUser. */
    useEffect(() => {
        const getAndParseActions = async (): Promise<void> => {
            setApiQueriesInProgress((current) => current + 1);
            try {
                const response = await props.actionsApi.getAllActions();

                const assignments: Assignment[] = response.data
                    .filter((action: Action) => !action.due_date) // Exclude Actions that have a due date, since those will be handled by the NotificationsApi query.
                    .filter((action: Action) => action.owner === selectedUser.cognito_subject) // Only include Actions assigned to the selectedUser.
                    .map((action: Action) => ({
                        id: action.id,
                        inputRequired: false,
                        title: action.title,
                        type: AssignmentType.ACTION,
                        url: generatePath(ACTION_DETAILS, { action_id: action.id }),
                    }));

                setActionAssignments(assignments);
            } catch (error) {
                setError(error);
            } finally {
                setApiQueriesInProgress((current) => current - 1);
            }
        };

        // Don't make the API call if the user doesn't have permission to the feature.
        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, RISK_ROLES)) {
            getAndParseActions();
        }
    }, [props.actionsApi, rbacContext, selectedUser]);

    /** When the component mounts or when the selectedUser changes, get Governance documents that are assigned to the selectedUser. */
    useEffect(() => {
        const getAndParseGovernance = async (): Promise<void> => {
            setApiQueriesInProgress((current) => current + 1);
            try {
                const response = await props.governanceApi.getAllActiveVersions();

                const assignments: Assignment[] = response.data
                    .filter((governance: GovernanceVersion) => governance.owner_user_id === selectedUser.cognito_subject) // Only include Governance assigned to the selectedUser.
                    .map((governance: GovernanceVersion) => ({
                        id: governance.id,
                        inputRequired: false,
                        title: governance.title,
                        type: AssignmentType.GOVERNANCE,
                        url: generatePath(GOVERNANCE_DETAILS, { governance_id: governance.id }),
                    }));

                setGovernanceAssignments(assignments);
            } catch (error) {
                setError(error);
            } finally {
                setApiQueriesInProgress((current) => current - 1);
            }
        };

        // Don't make the API call if the user doesn't have permission to the feature.
        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, CONTROL_AUTOMATION_ROLES)) {
            getAndParseGovernance();
        }
    }, [props.governanceApi, rbacContext, selectedUser]);

    /** When the component mounts or when the selectedUser changes, get Notifications that are assigned to the selectedUser. */
    useEffect(() => {
        /** Convert a NotificationType to a AssignmentType. */
        const convertNotificationTypeToAssignmentType = (notificationType: NotificationType): AssignmentType => {
            switch (notificationType) {
                case NotificationType.ACTION_DUE:
                    return AssignmentType.ACTION;
                case NotificationType.CONTROL_ASSESSMENT:
                    return AssignmentType.CONTROL_ASSESSMENT;
                case NotificationType.EXCEPTION_EXPIRY:
                    return AssignmentType.EXCEPTION;
                case NotificationType.TPRM_FOLDER_EXPIRY:
                    return AssignmentType.THIRD_PARTY_FOLDER;
                case NotificationType.TPRM_IRQ_DUE:
                    return AssignmentType.THIRD_PARTY_SERVICE_IRQ;
                case NotificationType.ISSUE_DUE:
                    return AssignmentType.ISSUE;
                case NotificationType.TPRM_DUE_DILIGENCE_WORKFLOW_DUE:
                    return AssignmentType.THIRD_PARTY_DUE_DILIGENCE_WORKFLOW;
                case NotificationType.TPRM_DUE_DILIGENCE_ASSESSMENT_DUE:
                    return AssignmentType.THIRD_PARTY_ASSESSMENT;
                case NotificationType.UCRM_DUE_DILIGENCE_WORKFLOW_DUE:
                    return AssignmentType.UCRM_DUE_DILIGENCE_WORKFLOW;
                case NotificationType.UCRM_FOLDER_EXPIRY:
                    return AssignmentType.UCRM_FOLDER;
                case NotificationType.UCRM_IRQ_DUE:
                    return AssignmentType.UCRM_USE_CASE_IRQ;
                case NotificationType.UCRM_DUE_DILIGENCE_ASSESSMENT_DUE:
                    return AssignmentType.UCRM_ASSESSMENT;
            }
        };

        /** Determine if the given Notification represents something awaiting input from the selectedUser, as opposed to a Notification a User may receive but is actually awaiting input from someone else (e.g, a Reviewer gets a Notification even though the Owner has not submitted something). */
        const determineIfNotificationRequiresUsersInput = (notification: UserNotification): boolean | undefined => {
            switch (notification._type) {
                // Actions only have one user assigned to them, and if there's a Notification then that implies ActionStatus.OPEN, so no need to actually check anything here.
                case NotificationType.ACTION_DUE:
                    return true;
                // Control Assessment delegates return undefined because we have no way to programmatically know if input is required from the owner or a delegate.
                case NotificationType.CONTROL_ASSESSMENT:
                    if (selectedUser.cognito_subject === notification.control.configuration.review_configuration?.owner_subject && notification.status === AssessmentState.IN_PROGRESS) {
                        return true;
                    } else if (notification.control.configuration.review_configuration?.delegate_subjects.includes(selectedUser.cognito_subject) && notification.status === AssessmentState.IN_PROGRESS) {
                        return undefined;
                    } else if (selectedUser.cognito_subject === notification.control.configuration.review_configuration?.reviewer_subject && notification.status === AssessmentState.UNDER_REVIEW) {
                        return true;
                    } else {
                        return false;
                    }
                case NotificationType.EXCEPTION_EXPIRY:
                    if (selectedUser.cognito_subject === notification.exception.owner_subject && (notification.exception.status === ExceptionStatus.DRAFT_OPEN || notification.exception.status === ExceptionStatus.APPROVED)) {
                        return true;
                    } else if (selectedUser.cognito_subject === notification.exception.reviewer_subject && notification.exception.status === ExceptionStatus.DRAFT_CLOSE) {
                        return true;
                    } else {
                        return false;
                    }
                // Third-Party Folders are implicitly assigned to the Third-Party Manager, and if there's a Notification then that implies there is an expiration date set, so no need to actually check anything here.
                case NotificationType.TPRM_FOLDER_EXPIRY:
                case NotificationType.UCRM_FOLDER_EXPIRY:
                    return true;
                // Third-Party IRQs are implicitly assigned to the Third-Party Service Manager, and if there's a Notification then that implies a target due date is set, so no need to actually check anything here.
                case NotificationType.TPRM_IRQ_DUE:
                case NotificationType.UCRM_IRQ_DUE:
                    return true;
                // Issue delegates return undefined because we have no way to programmatically know if input is required from the owner or a delegate.
                case NotificationType.ISSUE_DUE:
                    if (selectedUser.cognito_subject === notification.issue.owner_subject) {
                        return true;
                    } else if (notification.issue.delegates.includes(selectedUser.cognito_subject)) {
                        return undefined;
                    } else {
                        return false;
                    }
                // Third-Party Service Due Diligence Workflows are implicitly assigned to the Third-Party Service Manager, and if there's a Notification then that implies a due date is set, so no need to actually check anything here.
                case NotificationType.TPRM_DUE_DILIGENCE_WORKFLOW_DUE:
                case NotificationType.UCRM_DUE_DILIGENCE_WORKFLOW_DUE:
                    return true;
                // Assessment delegates return undefined because we have no way to programmatically know if input is required from delegates.
                case NotificationType.TPRM_DUE_DILIGENCE_ASSESSMENT_DUE:
                case NotificationType.UCRM_DUE_DILIGENCE_ASSESSMENT_DUE:
                    if (selectedUser.cognito_subject === notification.assessment.owner_id) {
                        return true;
                    } else if (notification.assessment.delegate_ids.includes(selectedUser.cognito_subject)) {
                        return undefined;
                    } else {
                        return false;
                    }
            }
        };

        const getAndParseNotifications = async (): Promise<void> => {
            setApiQueriesInProgress((current) => current + 1);
            try {
                // Get Notifications for the current User, or a different User if an admin is viewing someone else's Assignments.
                let response;
                if (selectedUser.cognito_subject === props.authHandler.getAuthenticatedUserSubject()) {
                    response = await props.notificationsApi.getNotificationsSelf(undefined, jsDateToIso8601(dateFilterHookValues.selection.dateRange.endDate));
                } else {
                    response = await props.notificationsApi.getNotificationsOther(selectedUser.cognito_subject, undefined, jsDateToIso8601(dateFilterHookValues.selection.dateRange.endDate));
                }

                const assignments: Assignment[] = response.data.map((notification) => ({
                    dueDate: notification.due_date,
                    id: notification.identifier,
                    inputRequired: determineIfNotificationRequiresUsersInput(notification),
                    title: getNotificationDisplayName(notification),
                    type: convertNotificationTypeToAssignmentType(notification._type),
                    url: getNotificationUrl(notification),
                }));

                setNotificationAssignments(assignments);
            } catch (error) {
                setError(error);
            } finally {
                setApiQueriesInProgress((current) => current - 1);
            }
        };

        getAndParseNotifications();
    }, [props.authHandler, props.notificationsApi, selectedUser, dateFilterHookValues.selection.dateRange.endDate]);

    /** When the component mounts or when the selectedUser changes, get Risks that are assigned to the selectedUser. */
    useEffect(() => {
        const getAndParseRisks = async (): Promise<void> => {
            setApiQueriesInProgress((current) => current + 1);
            try {
                const response = await props.riskRegisterApi.getAllRisks();

                const assignments: Assignment[] = response.data
                    .filter((risk: RiskResponse) => risk.owner === selectedUser.cognito_subject) // Only include Risks assigned to the selectedUser.
                    .map((risk: RiskResponse) => ({
                        id: risk.id,
                        inputRequired: false,
                        title: risk.title,
                        type: AssignmentType.RISK,
                        url: generatePath(RISK_DETAILS, { risk_id: risk.id }),
                    }));

                setRiskAssignments(assignments);
            } catch (error) {
                setError(error);
            } finally {
                setApiQueriesInProgress((current) => current - 1);
            }
        };

        // Don't make the API call if the user doesn't have permission to the feature.
        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, RISK_ROLES)) {
            getAndParseRisks();
        }
    }, [props.riskRegisterApi, rbacContext, selectedUser]);

    /** When the component mounts or when the selectedUser changes, get TPRM Third Parties, Folders, Service IRQs, Due Diligence Assessments, and Service Due Diligence Workflows that do not have a due date and are assigned to the selectedUser. */
    useEffect(() => {
        const getAndParseTprmAssignments = async (): Promise<void> => {
            setApiQueriesInProgress((current) => current + 1);
            try {
                const [thirdPartiesResponse, foldersResponse, assessmentSummariesResponse] = await Promise.all([props.tprmApi.getThirdParties(), props.tprmApi.getAllCurrentFolders(), props.tprmAssessmentApi.getAllAssessmentSummariesForAllServices()]);
                setTprmAssignments(parseModuleAssignments(TPRM_ASSIGNMENT_CONFIGURATION, thirdPartiesResponse.data, foldersResponse.data, assessmentSummariesResponse.data, selectedUser.cognito_subject));
            } catch (error) {
                setError(error);
            } finally {
                setApiQueriesInProgress((current) => current - 1);
            }
        };

        // Don't make the API call if the user doesn't have permission to the feature.
        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, TPRM_ROLES)) {
            getAndParseTprmAssignments();
        }
    }, [props.tprmApi, props.tprmAssessmentApi, rbacContext, selectedUser]);

    /** When the component mounts or when the selectedUser changes, get UCRM Businesses, Folders, Use Case IRQs, Due Diligence Assessments, and Use Case Due Diligence Workflows that do not have a due date and are assigned to the selectedUser. */
    useEffect(() => {
        const getAndParseUcrmAssignments = async (): Promise<void> => {
            setApiQueriesInProgress((current) => current + 1);
            try {
                const [businessesResponse, foldersResponse, assessmentSummariesResponse] = await Promise.all([props.ucrmApi.getBusinesses(), props.ucrmApi.getAllCurrentFolders(), props.ucrmAssessmentApi.getAllAssessmentSummariesForAllUseCases()]);
                setUcrmAssignments(parseModuleAssignments(UCRM_ASSIGNMENT_CONFIGURATION, businessesResponse.data, foldersResponse.data, assessmentSummariesResponse.data, selectedUser.cognito_subject));
            } catch (error) {
                setError(error);
            } finally {
                setApiQueriesInProgress((current) => current - 1);
            }
        };

        // Don't make the API call if the client doesn't have the feature or the user doesn't have permission to it.
        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, UCRM_ROLES) && clientHasUcrm) {
            getAndParseUcrmAssignments();
        }
    }, [clientHasUcrm, props.ucrmApi, props.ucrmAssessmentApi, rbacContext, selectedUser]);

    const combinedAssignments = useMemo(() => {
        if (apiQueriesInProgress > 0) {
            return undefined;
        }
        return [...actionAssignments, ...governanceAssignments, ...notificationAssignments, ...ucrmAssignments, ...riskAssignments, ...tprmAssignments];
    }, [actionAssignments, governanceAssignments, notificationAssignments, ucrmAssignments, riskAssignments, tprmAssignments, apiQueriesInProgress]);

    const columns: ColumnConfig<Assignment>[] = [
        {
            type: ColumnType.STRING,
            name: 'Title',
            getString: (assignment) => assignment.title,
            getOnClickTextAction: (assignment) => ({ goToLocation: assignment.url }),
        },
        {
            type: ColumnType.STRING,
            name: 'Type',
            getString: (assignment) => assignment.type,
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Input Required',
            getValue: (assignment) => (assignment.inputRequired === undefined ? 2 : assignment.inputRequired ? 1 : 0),
            sortComparator: (a, b) => (a as number) - (b as number),
            getTableCellContent: (assignment) => {
                switch (assignment.inputRequired) {
                    case true:
                        return (
                            <span title={userInputRequiredText} aria-label={userInputRequiredText} role="img">
                                <FontAwesomeIcon className={styles.yellow} icon={ICON_ALERT_WARNING} size="lg" />
                            </span>
                        );
                    case false:
                        return (
                            <span title={userInputNotRequiredText} aria-label={userInputNotRequiredText} role="img">
                                <FontAwesomeIcon className={styles.white} icon={ICON_NEUTRAL} size="lg" />
                            </span>
                        );
                    default:
                        return (
                            <span title={userInputRequiredUnknownText} aria-label={userInputRequiredUnknownText} role="img">
                                <FontAwesomeIcon className={styles.yellow} icon={ICON_UNKNOWN} size="lg" />
                            </span>
                        );
                }
            },
        },
        {
            type: ColumnType.DATE,
            name: 'Due Date',
            getDateString: (assignment) => assignment.dueDate,
            getIcon: (assignment) =>
                assignment.dueDate && new Date(assignment.dueDate) < new Date() ? (
                    <span title="This assignment's workflow is overdue." aria-label="This assignment's workflow is overdue." role="img">
                        <FontAwesomeIcon className={styles.red} icon={ICON_ALERT_WARNING} size="lg" style={{ marginLeft: 10 }} />
                    </span>
                ) : undefined,
        },
    ];

    // The sortBy column is applied first, then due date, then type, then title (as applicable). Sorting is applied to multiple columns in this way for improved readability when there are many identical values.
    const columnSubsortOrder = ['Due Date', 'Type', 'Title'];

    const tableFilters: FilterConfig<Assignment>[] = [
        {
            type: FilterType.SINGLE_PROPERTY,
            id: 'typeFilter',
            label: 'Filter by Type',
            hideLabel: true,
            options: [
                { name: 'All Types', value: 'All Types' },
                { name: AssignmentType.ACTION, value: AssignmentType.ACTION },
                { name: AssignmentType.CONTROL_ASSESSMENT, value: AssignmentType.CONTROL_ASSESSMENT },
                { name: AssignmentType.EXCEPTION, value: AssignmentType.EXCEPTION },
                { name: AssignmentType.ISSUE, value: AssignmentType.ISSUE },
                ...(clientHasUcrm
                    ? [
                          { name: AssignmentType.UCRM_BUSINESS, value: AssignmentType.UCRM_BUSINESS },
                          { name: AssignmentType.UCRM_ASSESSMENT, value: AssignmentType.UCRM_ASSESSMENT },
                          { name: AssignmentType.UCRM_FOLDER, value: AssignmentType.UCRM_FOLDER },
                          { name: AssignmentType.UCRM_DUE_DILIGENCE_WORKFLOW, value: AssignmentType.UCRM_DUE_DILIGENCE_WORKFLOW },
                          { name: AssignmentType.UCRM_USE_CASE_IRQ, value: AssignmentType.UCRM_USE_CASE_IRQ },
                      ]
                    : []),
                { name: AssignmentType.THIRD_PARTY, value: AssignmentType.THIRD_PARTY },
                { name: AssignmentType.THIRD_PARTY_ASSESSMENT, value: AssignmentType.THIRD_PARTY_ASSESSMENT },
                { name: AssignmentType.THIRD_PARTY_FOLDER, value: AssignmentType.THIRD_PARTY_FOLDER },
                { name: AssignmentType.THIRD_PARTY_DUE_DILIGENCE_WORKFLOW, value: AssignmentType.THIRD_PARTY_DUE_DILIGENCE_WORKFLOW },
                { name: AssignmentType.THIRD_PARTY_SERVICE_IRQ, value: AssignmentType.THIRD_PARTY_SERVICE_IRQ },
            ],
            // Include 'All Types' in the values for a row so all rows are included when "All Types" is selected.
            getFilterableDataForRow: (assignment) => [assignment.type, 'All Types'],
            defaultSelections: ['All Types'],
            preventClearing: true,
        },
        {
            type: FilterType.SINGLE_PROPERTY,
            id: 'workflowStatusFilter',
            label: 'Filter by Workflow State',
            hideLabel: true,
            options: [
                { name: InputRequiredFilterValues.ANY_WORKFLOW_STATE, value: InputRequiredFilterValues.ANY_WORKFLOW_STATE },
                { name: InputRequiredFilterValues.AWAITING_INPUT, value: InputRequiredFilterValues.AWAITING_INPUT },
            ],
            getFilterableDataForRow: (assignment) => {
                if (assignment.inputRequired === true || assignment.inputRequired === undefined) {
                    return [InputRequiredFilterValues.AWAITING_INPUT, InputRequiredFilterValues.ANY_WORKFLOW_STATE];
                }
                return [InputRequiredFilterValues.ANY_WORKFLOW_STATE];
            },
            defaultSelections: [InputRequiredFilterValues.AWAITING_INPUT],
            preventClearing: true,
        },
        {
            type: FilterType.SINGLE_PROPERTY,
            id: 'dueDateFilter',
            label: 'Filter by Due Date',
            hideLabel: true,
            options: [
                { name: DueDateFilterValues.HIDE_NA_DUE_DATES, value: DueDateFilterValues.HIDE_NA_DUE_DATES },
                { name: DueDateFilterValues.SHOW_NA_DUE_DATES, value: DueDateFilterValues.SHOW_NA_DUE_DATES },
            ],
            getFilterableDataForRow: (assignment) => {
                if (assignment.dueDate !== undefined) {
                    return [DueDateFilterValues.HIDE_NA_DUE_DATES, DueDateFilterValues.SHOW_NA_DUE_DATES];
                }
                return [DueDateFilterValues.SHOW_NA_DUE_DATES];
            },
            defaultSelections: [DueDateFilterValues.HIDE_NA_DUE_DATES],
            preventClearing: true,
        },
    ];

    const renderHeaderAndFilters = (): JSX.Element => {
        return (
            <div className={styles.header}>
                <div className={styles.title}>
                    <Text variant="Header2" color="white">
                        {pageTitle}
                    </Text>
                </div>
                <div className={styles.dataSelectionFiltersContainer}>
                    <RBACComponent roles={[Role.ADMIN]}>
                        <div className={styles.userFilter}>
                            {combinedAssignments === undefined ? (
                                <Skeleton variant="rectangular" height={38} />
                            ) : (
                                <FormFieldUserSelect
                                    colorTheme="dark"
                                    formFieldId="selectedUser"
                                    notClearable
                                    onUserSelected={(user: UserResponse | undefined, _formFieldId: string): void => {
                                        if (user) {
                                            setSelectedUser(user);
                                        }
                                    }}
                                    selectedUser={selectedUser}
                                />
                            )}
                        </div>
                    </RBACComponent>
                    <div className={styles.dateFilter}>{combinedAssignments === undefined ? <Skeleton variant="rectangular" height={38} /> : <FormFieldDateOptionSelect formFieldId="date" options={MyAssignmentsDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />}</div>
                </div>
            </div>
        );
    };

    if (error) {
        // There was an error while querying the API.
        return (
            <PageLayoutDashboard
                headerTitle={pageTitle}
                dashlets={[
                    {
                        content: (
                            <Text color="white" noStyles>
                                {error.message}
                            </Text>
                        ),
                    },
                ]}
            />
        );
    } else if (combinedAssignments && combinedAssignments.length === 0) {
        // The User has no Assignments.
        return (
            <PageLayoutDashboard
                headerTitle={pageTitle}
                dashlets={[
                    {
                        content: (
                            <>
                                <>{renderHeaderAndFilters()}</>
                                <Text color="white" noStyles>
                                    {userHasNoAssignmentsText}
                                </Text>
                            </>
                        ),
                    },
                ]}
            />
        );
    }

    return (
        <>
            {modalState === Modals.CUSTOM_DATE && <CustomDateModal date={dateFilterHookValues.selection.dateRange.endDate} hideModal={() => setModalState(Modals.NONE)} instructions="Assigned items with due dates prior to the selected date will be shown." dateFilterHookValues={dateFilterHookValues} />}

            <PageLayoutDashboard
                headerTitle={pageTitle}
                dashlets={[
                    {
                        content: (
                            <>
                                {renderHeaderAndFilters()}
                                <Table<Assignment> columns={columns} defaultSortColumn="Due Date" columnSubsortOrder={columnSubsortOrder} defaultSortDirection="ascending" filters={tableFilters} getUniqueIdForRow={(assignment) => assignment.id} rowsData={combinedAssignments} pageVariant="dashboard" />
                            </>
                        ),
                    },
                ]}
            />
        </>
    );
};
